<template>
    <div v-if="imageViewIsOpen && imageSrc && imageAltText" class="fixed inset-0 z-[9999] overflow-hidden">
        <div class="absolute inset-0 bg-[rgba(0,0,0,0.72)] backdrop-blur-lg transition-opacity duration-[500ms] ease-[cubic-bezier(0.22,1,0.36,1)]" :class="isExpanded ? 'opacity-100' : 'opacity-0'"></div>
        <div class="absolute inset-x-0 top-0 z-10 h-12 shrink-0 bg-[rgba(0,0,0,0.7)] backdrop-blur-lg flex flex-row justify-between items-center px-4 transition-opacity duration-[500ms] ease-[cubic-bezier(0.22,1,0.36,1)]" :class="isExpanded ? 'opacity-100' : 'opacity-0'">
            <span class="text-2xl text-[#eaf2ef] drop-shadow-[0_0_3px_#a8c3b4]">{{ imageTitle ?? imageAltText }}</span>
            <button class="hamburger block relative flex items-center justify-center w-12 h-12 -mr-4 grid" aria-label="Close image viewer" @click="closeImageMenu">
                <span class="hamburger-item w-8 h-[3px] absolute bg-[#eaf2ef] drop-shadow-[0_0_3px_#a8c3b4] rounded-md rotate-45"></span>
                <span class="w-8 h-[3px] absolute bg-[#eaf2ef] drop-shadow-[0_0_3px_#a8c3b4] rounded-md -rotate-45"></span>
            </button>
        </div>
        <div class="absolute inset-0 z-[10000] pointer-events-none">
            <div
                class="viewer-card fixed overflow-hidden transition-[top,left,width,height,transform,border-radius,opacity] duration-[500ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                :style="cardStyle"
                @transitionend="handleTransitionEnd"
            >
                <img :src="imageSrc" :alt="imageAltText" class="viewer-image" />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useImageViewer } from '../composables/useImageViewer';
const {
    imageViewIsOpen,
    imageSrc,
    imageAltText,
    imageTitle,
    imageSourceRect,
    viewerPhase,
    closeImageMenu,
    markImageMenuOpen,
    finalizeImageMenuClose,
} = useImageViewer();

const isExpanded = ref(false);
const viewportWidth = ref(window.innerWidth);
const viewportHeight = ref(window.innerHeight);
const headerHeight = 48;
const pagePadding = 32;
const expandedCornerRadius = 28;
const collapsedCornerRadius = 20;

const handleResize = () => {
    viewportWidth.value = window.innerWidth;
    viewportHeight.value = window.innerHeight;
};

const sourceStyle = computed(() => {
    const sourceRect = imageSourceRect.value;

    if (!sourceRect) {
        return {
            top: '50%',
            left: '50%',
            width: '1px',
            height: '1px',
            transform: 'translate(-50%, -50%)',
            borderRadius: `${collapsedCornerRadius}px`,
            opacity: '0',
        };
    }

    return {
        top: `${sourceRect.top}px`,
        left: `${sourceRect.left}px`,
        width: `${sourceRect.width}px`,
        height: `${sourceRect.height}px`,
        transform: 'translate(0, 0)',
        borderRadius: `${collapsedCornerRadius}px`,
        opacity: '1',
    };
});

const targetStyle = computed(() => {
    const sourceRect = imageSourceRect.value;

    if (!sourceRect) {
        return {
            top: `${headerHeight + pagePadding}px`,
            left: `${pagePadding}px`,
            width: `${viewportWidth.value - pagePadding * 2}px`,
            height: `${viewportHeight.value - headerHeight - pagePadding * 2}px`,
            transform: 'translate(0, 0)',
            borderRadius: `${expandedCornerRadius}px`,
            opacity: '1',
        };
    }

    const aspectRatio = sourceRect.width / sourceRect.height;
    const availableWidth = Math.max(240, viewportWidth.value - pagePadding * 2);
    const availableHeight = Math.max(240, viewportHeight.value - headerHeight - pagePadding * 2);

    let width = availableWidth;
    let height = width / aspectRatio;

    if (height > availableHeight) {
        height = availableHeight;
        width = height * aspectRatio;
    }

    const top = headerHeight + pagePadding + (availableHeight - height) / 2;
    const left = (viewportWidth.value - width) / 2;

    return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
        transform: 'translate(0, 0)',
        borderRadius: `${expandedCornerRadius}px`,
        opacity: '1',
    };
});

const cardStyle = computed(() => ({
    ...(isExpanded.value ? targetStyle.value : sourceStyle.value),
}));

watch(
    viewerPhase,
    async (phase) => {
        if (phase === 'opening') {
            isExpanded.value = false;
            await nextTick();
            requestAnimationFrame(() => {
                isExpanded.value = true;
            });
            return;
        }

        if (phase === 'closing') {
            isExpanded.value = false;
            return;
        }

        if (phase === 'closed') {
            isExpanded.value = false;
        }
    },
    { immediate: true }
);

onMounted(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

const handleTransitionEnd = (event: TransitionEvent) => {
    if (!['top', 'left', 'width', 'height', 'transform', 'opacity'].includes(event.propertyName)) {
        return;
    }

    if (viewerPhase.value === 'opening' && isExpanded.value) {
        markImageMenuOpen();
        return;
    }

    if (viewerPhase.value === 'closing' && !isExpanded.value) {
        finalizeImageMenuClose();
    }
};
</script>

<style scoped>
.viewer-card {
    will-change: top, left, width, height, transform, border-radius, opacity;
    background: transparent;
    box-shadow: none;
}

.viewer-image {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    border-radius: inherit;
}

.hamburger:hover {
  color: #b0cfc2; 
  text-shadow: 0px 0px 3px #90b7a7;
  transform: scale(1.0375);
  cursor: pointer;
}
</style>